import { ClipboardCheck as CardIconClipboardCheck } from 'lucide-react';
import { CardHeader as ContentCardHeader } from '@hue/design-system';
import { Select, StatusPill, useSnackbar } from '@hue/design-system';
import { CircleCheck } from 'lucide-react';
import { getApiError } from '../../lib/api.js';
import { formatDateTime } from '../../lib/time-zone.js';
import { ErrorCard, LoadingCard } from '../../app/data-view.js';
import type { ClosingProtocol, ProtocolPlannedMaterial } from './use-closing-protocol.js';

/// A seção de protocolo do fechamento. Depois de aplicado, ou com o atendimento concluído, é só
/// leitura: o retrato das etapas é o que aconteceu.
export function ClosingProtocolSection({
  protocol,
  canManage,
  locked,
  timeZone,
  onChosen,
}: {
  protocol: ClosingProtocol;
  canManage: boolean;
  /// Concluído: o protocolo não muda mais (ADR 0030).
  locked: boolean;
  timeZone: string;
  /// Avisado depois que a escolha foi gravada, com o previsto do escolhido.
  onChosen: (planned: ProtocolPlannedMaterial[] | null) => void;
}) {
  const snackbar = useSnackbar();
  const application = protocol.application;
  const chosen = protocol.protocols.find((item) => item.id === protocol.chosenId);
  const header = (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <ContentCardHeader
          title="Protocolo aplicado"
          id="closing-protocol-title"
          icon={<CardIconClipboardCheck className="size-4" />}
        />
        <p className="mt-1 max-w-prose text-xs text-content-muted">
          {protocol.applied || locked
            ? 'Retrato das etapas e do material previsto no instante da aplicação.'
            : 'Escolha o protocolo usado. Ele é aplicado quando você concluir.'}
        </p>
      </div>
      {protocol.applied ? (
        <StatusPill tone="positive">Aplicado</StatusPill>
      ) : protocol.chosenId ? (
        <StatusPill tone="warning">Será aplicado</StatusPill>
      ) : (
        <StatusPill tone="neutral">Sem protocolo</StatusPill>
      )}
    </div>
  );

  if (protocol.loading) {
    return (
      <section aria-labelledby="closing-protocol-title" className="grid gap-4">
        {header}
        <LoadingCard label="Carregando protocolo" />
      </section>
    );
  }
  if (protocol.error) {
    return (
      <section aria-labelledby="closing-protocol-title" className="grid gap-4">
        {header}
        <ErrorCard message={getApiError(protocol.error)} />
      </section>
    );
  }

  const steps =
    protocol.applied && application
      ? application.steps.map((step) => ({
          id: step.id,
          title: step.title,
          description: step.description,
          minutes: step.estimatedMinutes,
        }))
      : (chosen?.steps ?? []).map((step) => ({
          id: step.id,
          title: step.title,
          description: step.description,
          minutes: step.estimatedMinutes,
        }));

  return (
    <section aria-labelledby="closing-protocol-title" className="grid gap-4">
      {header}
      {protocol.applied && application ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-positive/30 bg-positive/5 p-3 text-xs text-content-secondary">
          <CircleCheck aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-positive" />
          <span>
            <strong className="text-content">{application.protocolDisplayName}</strong> aplicado
            {application.appliedByDisplayName ? ` por ${application.appliedByDisplayName}` : ''}
            {application.appliedAt ? ` em ${formatDateTime(application.appliedAt, timeZone)}` : ''}.
            {application.plannedProtocolDisplayName
              ? ` O planejado era ${application.plannedProtocolDisplayName}.`
              : ''}
          </span>
        </div>
      ) : locked ? (
        <p className="text-sm text-content-muted">Concluído sem protocolo.</p>
      ) : (
        <Select
          label="Protocolo"
          value={protocol.chosenId ?? ''}
          disabled={!canManage}
          options={[
            { value: '', label: 'Nenhum protocolo' },
            ...protocol.protocols.map((item) => ({ value: item.id, label: item.displayName })),
          ]}
          onValueChange={(value) => {
            const next = value || null;
            if (next === protocol.chosenId) return;
            void protocol
              .choose(next)
              .then(onChosen)
              .catch((cause: unknown) => snackbar.failure(getApiError(cause)));
          }}
        />
      )}
      {steps.length > 0 ? (
        <ol
          aria-label="Etapas"
          className="divide-y divide-surface-border rounded-xl border border-surface-border"
        >
          {steps.map((step, index) => (
            <li key={step.id} className="flex items-start gap-3 px-3 py-2.5">
              <span className="grid size-6 shrink-0 place-items-center rounded-full bg-surface-elevated text-xs font-semibold tabular-nums text-content-secondary">
                {index + 1}
              </span>
              <span className="min-w-0 flex-1 text-sm">
                <strong className="block font-semibold">{step.title}</strong>
                {step.description ? (
                  <span className="block text-xs text-content-muted">{step.description}</span>
                ) : null}
              </span>
              {step.minutes ? (
                <span className="shrink-0 text-xs tabular-nums text-content-muted">
                  {step.minutes} min
                </span>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}
